'use client'

import { FC, useState } from 'react'
import { usePathname } from 'next/navigation'
import Link from 'next/link'
import type { SideNavProps, SideNavItem } from './types'
import { NavItem, Label, Tooltip } from '@sylveraio/design-system'
import clsx from 'clsx'
import { CollapseButton } from '../CollapseButton'
import SylveraIcon from '@sylveraio/untitled-ui-icons-react/build/esm/SylveraIcon'
import Sylvera from '@sylveraio/untitled-ui-icons-react/build/esm/Sylvera'
import Share03 from '@sylveraio/untitled-ui-icons-react/build/esm/Share03'
import { UserProfile } from '../UserProfile'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { setCookie } from './utils/cookies'
import { DEFAULT_COLLAPSED_PATHS, GROUP_FADE_STYLE } from './constants'
import { sendAnalyticsEvent } from '@sylveraio/react-utils'

export const SideNav: FC<SideNavProps> = ({
  testId = 'side-nav',
  defaultCollapsed,
  primaryNavigationItems = [],
  secondaryNavigationItems = [],
  userProfile,
}) => {
  const currentPath = usePathname()
  const [collapsed, setCollapsed] = useState(
    defaultCollapsed ||
      DEFAULT_COLLAPSED_PATHS.some((page) => currentPath?.startsWith(page)),
  )
  const { newFeatureLabel } = useFlags<{
    newFeatureLabel: { paths: Array<string> }
  }>()

  const { paths: newFeatureLabelPaths } = newFeatureLabel || {}

  const handleCollapseClick = () => {
    setCollapsed((prev) => {
      const newState = !prev
      setCookie('sideNavState', newState ? 'collapsed' : 'expanded')
      return newState
    })

    sendAnalyticsEvent({
      eventName: `app-navigation-${!collapsed ? 'collapsed' : 'expanded'}`,
    })
  }

  const handleNavItemClick = (appName: string) => {
    sendAnalyticsEvent({
      eventName: 'app-navigation-go-to-app',
      eventData: {
        appName,
      },
    })
  }

  const separator = (
    <div className="my-1 h-px shrink-0 border-b border-default" />
  )

  const renderNavItems = (items: Array<SideNavItem>, withSeparator?: boolean) =>
    items
      .filter(({ hide }) => !hide)
      .map(({ icon, title, path, secondaryPaths = [], childItems, newTab }) => {
        const paths = [path, ...secondaryPaths]
        const isSelected = paths.some((p) =>
          p === '/' ? currentPath === p : currentPath?.startsWith(p),
        )
        const newLabel =
          !collapsed && newFeatureLabelPaths?.includes(path) ? (
            <Label color="discovery" size="sm" label="New" strong ping />
          ) : undefined

        return (
          <div key={path}>
            <Tooltip
              triggerClassName="!w-full"
              title={title}
              condensed
              subtle={false}
              placement="right"
              disable={!collapsed}
            >
              <NavItem
                title={title}
                icon={icon}
                iconTrailing={newTab ? <Share03 /> : undefined}
                labelTrailing={newLabel}
                isSelected={isSelected}
                textClassName={GROUP_FADE_STYLE}
                href={path}
                newTab={newTab}
                onClick={() => handleNavItemClick(title)}
              />
            </Tooltip>

            {childItems &&
              childItems
                .filter(({ hide }) => !hide)
                .map(({ icon, title, path, secondaryPaths = [], newTab }) => {
                  const childPaths = [path, ...secondaryPaths]
                  const childIsSelected = childPaths.some((p) =>
                    p === '/' ? currentPath === p : currentPath?.startsWith(p),
                  )

                  const newLabel =
                    !collapsed && newFeatureLabelPaths?.includes(path) ? (
                      <Label
                        color="discovery"
                        size="sm"
                        label="New"
                        strong
                        ping
                      />
                    ) : undefined

                  return (
                    <Tooltip
                      key={path}
                      triggerClassName="!w-full"
                      condensed
                      subtle={false}
                      title={title}
                      placement="right"
                      disable={!collapsed}
                    >
                      <NavItem
                        title={title}
                        icon={icon}
                        iconTrailing={newTab ? <Share03 /> : undefined}
                        labelTrailing={newLabel}
                        isSelected={childIsSelected}
                        textClassName={GROUP_FADE_STYLE}
                        href={path}
                        newTab={newTab}
                        onClick={() => handleNavItemClick(title)}
                      />
                    </Tooltip>
                  )
                })}
            {withSeparator && separator}
          </div>
        )
      })

  return (
    <div
      data-testid={testId}
      className={clsx(
        'flex flex-col bg-medium border-r border-default transition-all ease-in-out duration-200 h-dvh',
        {
          'w-60': !collapsed,
          'group is-collapsed': collapsed,
          'w-[52px]': collapsed,
        },
      )}
    >
      <div className="py-2">
        <Link
          href="/"
          className={clsx('flex items-center pt-2 pb-1', {
            'px-4 justify-center': collapsed,
            'pl-4 pr-2 justify-start': !collapsed,
          })}
        >
          {collapsed ? (
            <SylveraIcon className="h-8 w-[30.5px] shrink-0 stroke-0" />
          ) : (
            <Sylvera className="h-8 w-32 shrink-0 stroke-0" />
          )}
        </Link>
      </div>

      {separator}

      <div className="flex w-full flex-1 flex-col overflow-y-auto overflow-x-hidden">
        <div>{renderNavItems(primaryNavigationItems, true)}</div>
        <div className="mt-auto">
          {renderNavItems(secondaryNavigationItems)}
        </div>
      </div>

      {separator}

      <Tooltip
        triggerClassName="!w-full"
        condensed
        subtle={false}
        title="Expand navigation"
        placement="right"
        disable={!collapsed}
        delay={{
          open: 350,
          close: 0,
        }}
        dismissOnReferencePress
      >
        <CollapseButton
          onClick={handleCollapseClick}
          collapsed={collapsed}
          title="Collapse navigation"
          textClassName={GROUP_FADE_STYLE}
        />
      </Tooltip>

      {separator}

      <UserProfile collapsed={collapsed} {...userProfile} />
    </div>
  )
}

export default SideNav
